import clsx from 'clsx';
import React, { ReactNode, useEffect } from 'react';

import { Icon } from '@/shared/ui/Icon/Icon';
import styles from './ModalStyle.module.scss';

type OverlayProps = {
    isVisible: boolean;
    backgroundColor?: string;
    children?: ReactNode;
};

const Overlay: React.FC<OverlayProps> = ({ children, isVisible, backgroundColor }) => (
    <div className={clsx(styles.overlay, styles[`is-${backgroundColor}`], { [styles['is-visible']]: isVisible })}>
        {children}
    </div>
);

type ModalCloseIconProps = {
    customBtn?: ReactNode;
    onClick: (_: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
};

export const ModalCloseIcon: React.FC<ModalCloseIconProps> = ({ onClick, customBtn }) => (
    <div
        className={styles.close}
        onClick={onClick}
        tabIndex={0}
        role={'button'}
        onKeyDown={e => {
            if (e.key === 'Enter') {
                onClick(e as any);
            }
        }}
    >
        {customBtn ?? <Icon name="close" className={styles.defaultIcon} />}
    </div>
);

type ModalProps = {
    className?: string;
    backgroundColor?: string;
    isOpen: boolean;
    maxWidth?: number;
    fullScreen?: boolean;
    children: ReactNode;
    closeButton?: ReactNode;
    onClose: () => void;
};

export const Modal: React.FC<ModalProps> = ({
    className,
    isOpen,
    maxWidth,
    fullScreen,
    onClose,
    children,
    backgroundColor,
    closeButton,
}) => {
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';

            return () => {
                document.body.style.overflow = 'unset';
            };
        }
    }, [isOpen]);

    return (
        <Overlay isVisible={isOpen} backgroundColor={backgroundColor}>
            <div
                className={clsx(
                    styles.modal,
                    className,
                    { [styles['is-open']]: isOpen },
                    { [styles['is-full-screen']]: fullScreen },
                )}
                style={{ maxWidth }}
            >
                <ModalCloseIcon onClick={onClose} customBtn={closeButton} />
                <div className={styles.content}>{children}</div>
            </div>
        </Overlay>
    );
};
